import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { withCache } from '@ngneat/cashew';
import { DatasourceUIModel } from '@y42/datasource';
import { isNotNullish } from '@y42/util/nullish';
import { distinctUntilChanged, forkJoin, map, Observable, shareReplay } from 'rxjs';
import { IntegrationService } from '../datasource/integration.service';
import { IntegrationTypeSecretJSONSchema, IntegrationTypeSourceJSONSchema } from './integration-type-json-schema.model';

@Injectable({ providedIn: 'root' })
export class IntegrationTypeJSONSchemaHttpService {
  constructor(private readonly http: HttpClient, private readonly integrationService: IntegrationService) {}

  selectActiveIntegrationJSONSchema(integration: DatasourceUIModel): Observable<{
    source: IntegrationTypeSourceJSONSchema;
    secret: IntegrationTypeSecretJSONSchema;
  } | null> {
    return forkJoin([
      this.http.get<IntegrationTypeSourceJSONSchema>(
        this.integrationService.getUrl(integration.typeModel.type, 'source'),
        {
          context: withCache({
            key: `datasource-model-type-source-${integration.typeModel.type}`,
          }),
        },
      ),
      this.http.get<IntegrationTypeSecretJSONSchema>(
        this.integrationService.getUrl(integration.typeModel.type, 'secret'),
        {
          context: withCache({
            key: `datasource-model-type-secret-${integration.typeModel.type}`,
          }),
        },
      ),
    ]).pipe(
      map(([source, secret]) => ({ source, secret })),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }

  selectSource(integration: DatasourceUIModel) {
    return this.selectActiveIntegrationJSONSchema(integration).pipe(
      map((jsons) => (jsons ? jsons.source : null)),
      distinctUntilChanged(),
    );
  }

  selectHasAccountSelect(integration: DatasourceUIModel) {
    return this.selectSource(integration).pipe(
      map(
        (source) =>
          source &&
          source.properties &&
          Object.keys(source.properties).some(
            (prop) => source.properties?.[prop]?.metadata?.from_resource === 'accounts',
          ),
      ),
      distinctUntilChanged(),
    );
  }

  selectHasFixedColumns(integration: DatasourceUIModel) {
    return this.selectSource(integration).pipe(
      map((source) => !!source?.settings?.hasFixedColumns),
      distinctUntilChanged(),
    );
  }

  selectAreSettingsValid(integration: DatasourceUIModel) {
    return this.selectSource(integration).pipe(
      map((source) => {
        if (!source?.required) {
          return true;
        }

        return source.required.every((field) => {
          const value = (integration.settings as unknown as Record<string, unknown>)[field];
          return isNotNullish(value) && value !== '';
        });
      }),
    );
  }
}
